<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/api'
import { useI18n } from '@/i18n'
import { useFormatDate } from '@/composables/useFormatDate'
import { flagFromISO2 } from '@/utils/country'
import { enrichPostRow } from '@/composables/useEnrichPost'

const props = defineProps({ groupId: { type: Number, required: true } })
const router = useRouter()
const { t } = useI18n()
const { fmtDate } = useFormatDate()

const loading = ref(false)
const error   = ref('')
const items   = ref([])
const total   = ref(0)

const columns = [
  { field: 'post_title', header: t('hackwatch.posts.colTitle'),   sortable: true  },
  { field: 'discovered', header: t('hackwatch.posts.colDiscovered'), sortable: true  },
  { field: 'domain',     header: t('hackwatch.posts.colDomain'),  sortable: false },
  { field: 'country',    header: t('hackwatch.posts.colCountry'), sortable: false },
]

const enrichRow = enrichPostRow

async function load(q = {}) {
  loading.value = true
  error.value = ''
  try {
    const params = {
      page:  q.page  ?? 1,
      limit: q.limit ?? 25,
      q:     q.search || undefined,
    }
    const { data } = await api.getGroupPosts(props.groupId, params)
    const rawRows = data.rows || data || []
    items.value = rawRows.map(enrichRow)
    total.value = data.total ?? items.value.length
  } catch (e) {
    console.error(e)
    error.value = t('hackwatch.posts.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(() => load())
watch(() => props.groupId, () => load())

function onQueryChange(q) { load(q) }

function openPost(id) { router.push(`/threat-news/posts/${id}`) }
</script>

<template>
  <div class="hw-card">
    <div class="hw-card-header">
      <i class="pi pi-file hw-card-icon" />
      <span class="hw-card-title">{{ t('hackwatch.groupDetail.legendPosts') }}</span>
    </div>
    <AppTableWidget
      :columns="columns"
      :data="items"
      :total="total"
      :loading="loading"
      lazy
      :searchable="true"
      :default-rows="10"
      :rows-options="[10, 25, 50]"
      data-key="post_id"
      :empty-text="t('hackwatch.groupDetail.noPosts')"
      @refresh="load()"
      @query-change="onQueryChange"
    >
      <template #cell-post_title="{ row }">
        <a class="hw-link" href="javascript:void(0)" @click="openPost(row.id || row.post_id)">
          {{ row.post_title }}
        </a>
      </template>
      <template #cell-discovered="{ row }">{{ fmtDate(row.discovered) }}</template>
      <template #cell-domain="{ row }">
        <a
          v-if="row.domain"
          :href="`https://${row.domain}`"
          target="_blank"
          rel="noopener noreferrer"
          class="hw-link"
        >{{ row.domain }}</a>
        <span v-else>—</span>
      </template>
      <template #cell-country="{ row }">
        <span v-if="row.country">
          {{ flagFromISO2(row.country) }}
          <small class="country-code">{{ row.country }}</small>
        </span>
        <span v-else>—</span>
      </template>
    </AppTableWidget>

    <div v-if="error" class="mt-1 text-sm" style="color: var(--p-danger)">{{ error }}</div>
  </div>
</template>

<style scoped>
.hw-link { color: var(--p-primary-color); cursor: pointer; text-decoration: none; }
.hw-link:hover { text-decoration: underline; }
.country-code { opacity: .7; margin-left: .2rem; }
</style>
